import { Link } from 'react-router-dom';
import {
  OVERVIEW_DISPLAY_STATE_LABELS,
  PERMISSION_GRID,
  overviewTargetPath,
  type OverviewCta,
  type OverviewDisplayState,
  type OverviewNodeId,
} from '@scoutaround/script-breakdown-shared';

import { Icon, type IconName } from '../Icon.js';
import { StatusPill } from '../ui/StatusPill.js';
import { useMembership } from '../../lib/permissions.js';

/** One icon per module, from the product's own set. */
export const NODE_ICON: Readonly<Record<OverviewNodeId, IconName>> = {
  SCREENPLAY: 'script',
  SCENES: 'scenes',
  LOCATIONS: 'pin',
  BREAKDOWN: 'model',
};

/**
 * A state, in the server's words, drawn in the workspace's one status vocabulary
 * (`StatusPill`, `.ui-pill[data-state]`) so the Overview and the screens it opens
 * colour the same state the same way.
 */
export function StatePill({ state, testId }: { state: OverviewDisplayState; testId?: string }) {
  return <StatusPill state={state} label={OVERVIEW_DISPLAY_STATE_LABELS[state]} testId={testId} />;
}

/**
 * Why a control is offered as a way to LOOK. The production's read-only state
 * first — it is the same for everyone — then the person's own role, by the
 * permission's label on the grid rather than its constant.
 */
export function useLookOnlyReason(readOnly: boolean): (cta: OverviewCta) => string | null {
  const membership = useMembership();

  return (cta) => {
    if (cta.executable) return null;
    if (readOnly) return 'This production is read-only. Renew it under Plan & billing to make changes.';

    const cell = PERMISSION_GRID.find((c) => c.permission === cta.requires);
    const what = cell === undefined ? 'this' : `“${cell.label}”`;
    const who = membership === null ? 'Your role' : `Your role (${membership.label})`;

    return `${who} can look but not do ${what} on this production. A producer can change that under Team.`;
  };
}

/**
 * A control that goes somewhere, as a real link: it opens in a new tab, reads
 * as a link to a screen reader, and keeps the production's address.
 *
 * NOT EXECUTABLE IS NOT HIDDEN. A role that may not confirm scenes may still
 * read them, so the link stays, says "View", and the reason sits beside it —
 * the server would refuse the act anyway; the screen does not pretend otherwise.
 */
export function CtaLink({
  address,
  cta,
  className,
  testId,
}: {
  address: string;
  cta: OverviewCta;
  className: string;
  testId: string;
}) {
  return (
    <Link
      to={overviewTargetPath(address, cta.target)}
      className={className}
      data-testid={testId}
      data-executable={cta.executable ? '1' : '0'}
    >
      {cta.executable ? cta.label : 'View'}
      <Icon name="arrowRight" size={14} />
    </Link>
  );
}

/** A count over a denominator, as a thin bar. An empty denominator draws no fill and no claim. */
export function RatioBar({
  value,
  of,
  tone,
  label,
}: {
  value: number;
  of: number;
  tone: 'ok' | 'warn' | 'ink';
  label: string;
}) {
  const share = of <= 0 ? 0 : Math.min(1, value / of);

  return (
    <div className="cc-bar" role="img" aria-label={label}>
      {share > 0 ? <span data-tone={tone} style={{ width: `${String(share * 100)}%` }} /> : null}
    </div>
  );
}

/** Several parts of one whole, side by side, each its own colour and count. */
export function StackBar({
  parts,
  label,
}: {
  parts: readonly { readonly value: number; readonly tone: 'ok' | 'warn' | 'danger' | 'ink' | 'muted' }[];
  label: string;
}) {
  const total = parts.reduce((sum, p) => sum + p.value, 0);

  return (
    <div className="cc-bar" role="img" aria-label={label}>
      {total === 0
        ? null
        : parts.map((p, i) =>
            p.value === 0 ? null : (
              <span key={i} data-tone={p.tone} style={{ width: `${String((p.value / total) * 100)}%` }} />
            ),
          )}
    </div>
  );
}

/**
 * A time a producer reads at a glance: "4 min ago" today, the date otherwise.
 * The full moment stays in the `title` and the `<time>` element.
 */
export function When({ at, now = Date.now() }: { at: string; now?: number }) {
  const then = Date.parse(at);
  const minutes = Math.round((now - then) / 60_000);
  const full = new Date(then).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  let text: string;
  if (minutes < 1) text = 'just now';
  else if (minutes < 60) text = `${String(minutes)} min ago`;
  else if (minutes < 24 * 60) text = `${String(Math.round(minutes / 60))} h ago`;
  else text = new Date(then).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <time dateTime={at} title={full}>
      {text}
    </time>
  );
}
